import { useMemo, useState } from 'react';
import { Drawer, DrawerSection, KeyValues, Segmented, Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { num, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

/** What one asset accumulated over the days chosen. */
interface Figures {
  tickets: number;
  openTickets: number;
  sharedTickets: number;
  ticketsWorked: number;
  hours: number;
  billableHours: number;
  nonBillableHours: number;
  entries: number;
  hoursPerTicket: number | null;
  lastTicket: string | null;
}
interface Asset extends Figures {
  id: number;
  companyId: number | null;
  account: string | null;
  accountNumber: string | null;
  accountActive: boolean | null;
  name: string;
  referenceNumber: string | null;
  serialNumber: string | null;
  type: string | null;
  category: string | null;
  product: string | null;
  contractId: number | null;
  location: string | null;
  hostname: string | null;
  installDate: string | null;
  warrantyExpiration: string | null;
}
interface Entry {
  id: number;
  date: string | null;
  resource: string | null;
  hours: number;
  workType: string | null;
  nonBillable: boolean;
  summary: string | null;
}
interface DetailTicket {
  id: number;
  ticketNumber: string | null;
  title: string | null;
  link: string | null;
  account: string | null;
  created: string | null;
  completed: string | null;
  raisedInRange: boolean;
  status: string | null;
  queue: string | null;
  issue: string | null;
  priority: string | null;
  assets: number;
  primary: boolean;
  hours: number;
  share: number;
  entries: Entry[];
}
interface Payload {
  from: string;
  to: string;
  assets: { total: number; touched: number; quiet: number };
  totals: Figures;
  retired: Figures;
  unassigned: Figures;
  rows: Asset[];
  detail: { id: number; name: string; from: string; to: string; tickets: DetailTicket[] } | null;
  notes: string[];
  fetchedUtc: string;
}

type Show = 'touched' | 'all';
type Sort = 'hours' | 'tickets' | 'hoursPerTicket' | 'account';

const hrs = (v: number) => num(v, 2);
const day = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—');

function quick(k: 'month' | 'lastMonth' | 'quarter' | 'year'): { from: string; to: string } {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (k === 'month') return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 1)), to: todayLocal() };
  if (k === 'lastMonth') {
    return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 0)) };
  }
  if (k === 'quarter') return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 2, 1)), to: todayLocal() };
  return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 11, 1)), to: todayLocal() };
}
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
  { k: 'quarter', label: 'Last 3 months' },
  { k: 'year', label: 'Last 12 months' },
];

/**
 * Reports › Asset Workload (routes/reportsConfigurationItems.ts).
 *
 * Every ACTIVE configuration item in Autotask with the tickets raised against
 * it in the days chosen and the hours put into them, so the devices that cost
 * the desk the most are visible next to the ones that cost nothing.
 *
 * THE DATE RANGE is the point of the page, so it is the first control on it:
 * tickets are those CREATED in the range, hours are those WORKED in it. The
 * two cohorts are read separately and the page says so — a ticket opened in
 * March and still being worked in May puts its May hours on the asset without
 * being counted as a May ticket.
 *
 * A ticket that names several assets counts on each of them and its hours
 * divide between them, which is why an asset's hours can be a fraction it
 * never worked alone. The totals tile counts every ticket once.
 *
 * Kept until Refresh — each read costs a round trip to Autotask.
 */
export function AssetWorkloadReportPage() {
  const [{ from, to }, setRange] = useState(() => quick('quarter'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [show, setShow] = useState<Show>('touched');
  const [sort, setSort] = useState<Sort>('hours');
  const [account, setAccount] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  /** The asset whose tickets are open in the drawer, or null. */
  const [open, setOpen] = useState<Asset | null>(null);

  const data = useHeldResource<Payload>(`/reports/configuration-items?${new URLSearchParams({ from, to })}`);
  /* The drill-in is its own read, held under its own key, so closing and
     reopening an asset costs nothing and the list is never re-read for it. */
  const drill = useHeldResource<Payload>(
    open ? `/reports/configuration-items?${new URLSearchParams({ from, to, configurationItemId: String(open.id) })}` : null,
  );

  const all = data.data?.rows ?? [];

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  const accounts = useMemo(
    () => [...new Set(all.map((r) => r.account).filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b)),
    [all],
  );
  const types = useMemo(() => [...new Set(all.map((r) => r.type).filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b)), [all]);

  const q = search.trim().toLowerCase();
  const rows = useMemo(() => {
    const kept = all.filter(
      (r) =>
        (show === 'all' || r.tickets > 0 || r.entries > 0) &&
        (!account || r.account === account) &&
        (!type || r.type === type) &&
        (!q ||
          [r.name, r.account, r.serialNumber, r.hostname, r.product, r.category, r.type, r.location, r.referenceNumber].some(
            (v) => v && v.toLowerCase().includes(q),
          )),
    );
    const by: Record<Sort, (a: Asset, b: Asset) => number> = {
      hours: (a, b) => b.hours - a.hours || b.tickets - a.tickets,
      tickets: (a, b) => b.tickets - a.tickets || b.hours - a.hours,
      hoursPerTicket: (a, b) => (b.hoursPerTicket ?? -1) - (a.hoursPerTicket ?? -1) || b.hours - a.hours,
      account: (a, b) => (a.account ?? '').localeCompare(b.account ?? '') || b.hours - a.hours,
    };
    return [...kept].sort((a, b) => by[sort](a, b) || a.name.localeCompare(b.name));
  }, [all, show, account, type, q, sort]);

  /* The tiles follow the filters: they describe what is on the screen, not
     what Autotask holds. The exception is the totals line below them, which
     is the desk's whole range with every ticket counted once. */
  const shown = useMemo(
    () => ({
      hours: rows.reduce((s, r) => s + r.hours, 0),
      tickets: rows.reduce((s, r) => s + r.tickets, 0),
      touched: rows.filter((r) => r.tickets > 0 || r.entries > 0).length,
      worst: rows.reduce<Asset | null>((w, r) => (w === null || r.hours > w.hours ? r : w), null),
    }),
    [rows],
  );

  const exportCsv = () =>
    downloadCsv(`asset_workload_${from}_to_${to}_${fileStamp()}.csv`, [
      [
        'Account', 'Account Number', 'Asset', 'Type', 'Category', 'Product', 'Serial Number', 'Hostname', 'Location',
        'Install Date', 'Warranty Expires', 'Tickets Raised', 'Still Open', 'Shared Tickets', 'Tickets Worked',
        'Hours', 'Billable Hours', 'Non-Billable Hours', 'Time Entries', 'Hours per Ticket', 'Last Ticket',
      ],
      ...rows.map((r) => [
        r.account, r.accountNumber, r.name, r.type, r.category, r.product, r.serialNumber, r.hostname, r.location,
        r.installDate, r.warrantyExpiration, r.tickets, r.openTickets, r.sharedTickets, r.ticketsWorked,
        r.hours, r.billableHours, r.nonBillableHours, r.entries, r.hoursPerTicket, r.lastTicket,
      ]),
    ]);

  const totals = data.data?.totals;
  const detail = drill.data?.detail ?? null;

  return (
    <>
      <PageHead
        title="Asset Workload"
        lede="Every active configuration item in Autotask with the tickets raised against it and the hours put into them, for the days you choose. Read live from Autotask."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="From">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((k) => (
                <button key={k.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(k.k))}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row wrap mt-2" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Assets">
              <Segmented
                label="Assets"
                value={show}
                onChange={setShow}
                options={[
                  { id: 'touched', label: `With activity (${num(data.data?.assets.touched ?? 0)})` },
                  { id: 'all', label: `All active (${num(data.data?.assets.total ?? 0)})` },
                ]}
              />
            </Field>
            <Field label="Sort by">
              <Segmented
                label="Sort by"
                value={sort}
                onChange={setSort}
                options={[
                  { id: 'hours', label: 'Hours' },
                  { id: 'tickets', label: 'Tickets' },
                  { id: 'hoursPerTicket', label: 'Hours per ticket' },
                  { id: 'account', label: 'Client' },
                ]}
              />
            </Field>
            <Field label="Client">
              <select className="select" value={account} onChange={(e) => setAccount(e.target.value)}>
                <option value="">Every client</option>
                {accounts.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Asset type">
              <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Every type</option>
                {types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Asset, client, serial, hostname" />
            </Field>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            Tickets created {day(from)} – {day(to)} that name the asset, and the hours worked in those same days — including hours on a ticket
            opened before the range. A ticket naming several assets counts on each and its hours divide between them.
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
          {data.data?.notes.map((n) => (
            <p key={n} className="xs muted mt-1">
              {n}.
            </p>
          ))}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !data.data && <Loading label="Reading assets, tickets and time from Autotask" />}

      {data.data && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Assets shown" value={num(rows.length)} foot={`${num(shown.touched)} with a ticket or an hour`} />
            <Stat label="Tickets raised" value={num(shown.tickets)} foot="on these assets, in these days" />
            <Stat label="Hours" value={hrs(shown.hours)} foot="worked on these assets, in these days" />
            <Stat
              label="Busiest asset"
              value={shown.worst && shown.worst.hours > 0 ? hrs(shown.worst.hours) : '—'}
              foot={shown.worst && shown.worst.hours > 0 ? `${shown.worst.name} · ${shown.worst.account ?? 'no client'}` : 'nothing logged'}
            />
          </div>

          {totals && (
            <section className="card mb-4">
              <div className="card-body">
                <p className="small">
                  Across the whole range the desk raised <strong>{num(totals.tickets)}</strong> tickets and logged{' '}
                  <strong>{hrs(totals.hours)}</strong> hours, each ticket counted once however many assets it touched.{' '}
                  {data.data.unassigned.tickets > 0 && (
                    <>
                      <strong>{num(data.data.unassigned.tickets)}</strong> of those tickets ({hrs(data.data.unassigned.hours)} hours) name no asset
                      at all, so they appear in no row below.{' '}
                    </>
                  )}
                  {data.data.retired.tickets > 0 && (
                    <>
                      <strong>{num(data.data.retired.tickets)}</strong> ({hrs(data.data.retired.hours)} hours) name an asset that is no longer
                      active, and that share of them is in no row below.{' '}
                    </>
                  )}
                  {data.data.assets.quiet > 0 && (
                    <>
                      <strong>{num(data.data.assets.quiet)}</strong> of the {num(data.data.assets.total)} active assets saw no ticket and no hour in
                      these days.
                    </>
                  )}
                </p>
              </div>
            </section>
          )}

          {rows.length === 0 ? (
            <section className="card">
              <div className="card-body">
                <p className="muted small">
                  No assets {account || type || q || show === 'touched' ? 'match' : 'were returned'}
                  {show === 'touched' && !account && !type && !q ? ' — nothing was raised against an asset in these days' : ''}.
                </p>
              </div>
            </section>
          ) : (
            <section className="card">
              <div className="table-wrap">
                <table className="table pp-table">
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Client</th>
                      <th>Type</th>
                      <th className="num">Tickets</th>
                      <th className="num">Open</th>
                      <th className="num">Hours</th>
                      <th className="num">Hrs/ticket</th>
                      <th>Last ticket</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <button type="button" className="link-button" onClick={() => setOpen(r)}>
                            {r.name}
                          </button>
                          {(r.serialNumber || r.hostname) && <div className="xs muted">{r.hostname ?? r.serialNumber}</div>}
                        </td>
                        <td>
                          {r.account ?? '—'}
                          {r.accountActive === false && <div className="xs muted">inactive client</div>}
                        </td>
                        <td>
                          {r.type ?? '—'}
                          {r.category && <div className="xs muted">{r.category}</div>}
                        </td>
                        <td className="num">
                          {num(r.tickets)}
                          {r.sharedTickets > 0 && <div className="xs muted">{num(r.sharedTickets)} shared</div>}
                        </td>
                        <td className="num">{num(r.openTickets)}</td>
                        <td className="num">
                          {hrs(r.hours)}
                          {r.ticketsWorked > r.tickets && <div className="xs muted">{num(r.ticketsWorked)} worked</div>}
                        </td>
                        <td className="num">{r.hoursPerTicket === null ? '—' : hrs(r.hoursPerTicket)}</td>
                        <td>{day(r.lastTicket)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      <Drawer
        open={open !== null}
        onClose={() => setOpen(null)}
        wide
        title={open?.name ?? ''}
        subtitle={[open?.account, open?.type, open?.serialNumber].filter(Boolean).join(' · ')}
      >
        {open && (
          <>
            <DrawerSection title="The asset">
              <KeyValues
                items={(
                  [
                    ['Client', open.account],
                    ['Type', open.type],
                    ['Category', open.category],
                    ['Product', open.product],
                    ['Serial number', open.serialNumber],
                    ['Hostname', open.hostname],
                    ['Location', open.location],
                    ['Installed', open.installDate ? day(open.installDate) : null],
                    ['Warranty expires', open.warrantyExpiration ? day(open.warrantyExpiration) : null],
                  ] as [string, string | null][]
                ).filter((pair): pair is [string, string] => !!pair[1])}
              />
            </DrawerSection>

            <DrawerSection title="In these days" count={`${num(open.tickets)} tickets`}>
              <div className="grid grid-3">
                <Stat label="Tickets raised" value={num(open.tickets)} foot={`${num(open.openTickets)} still open`} />
                <Stat label="Hours" value={hrs(open.hours)} foot={`${hrs(open.billableHours)} billable`} />
                <Stat label="Hours per ticket" value={open.hoursPerTicket === null ? '—' : hrs(open.hoursPerTicket)} foot="raised in these days" />
              </div>
            </DrawerSection>

            {drill.error && <ErrorBanner message={drill.error} />}
            {drill.loading && !detail && <Loading label="Reading this asset’s tickets" />}

            {detail && (
              <DrawerSection title="Tickets" count={num(detail.tickets.length)}>
                {detail.tickets.length === 0 ? (
                  <p className="muted small">No ticket named this asset in these days.</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Ticket</th>
                          <th>Created</th>
                          <th>Status</th>
                          <th className="num">Hours</th>
                          <th className="num">This asset</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.tickets.map((t) => (
                          <tr key={t.id}>
                            <td>
                              {t.link ? (
                                <a href={t.link} target="_blank" rel="noreferrer">
                                  {t.ticketNumber ?? `Ticket ${t.id}`}
                                </a>
                              ) : (
                                (t.ticketNumber ?? `Ticket ${t.id}`)
                              )}
                              <div className="xs muted">{t.title ?? '—'}</div>
                              {!t.primary && <div className="xs muted">attached, not the primary asset</div>}
                              {!t.raisedInRange && <div className="xs muted">opened before these days; worked in them</div>}
                            </td>
                            <td>{day(t.created)}</td>
                            <td>
                              {t.status ?? '—'}
                              {t.queue && <div className="xs muted">{t.queue}</div>}
                            </td>
                            <td className="num">{hrs(t.hours)}</td>
                            <td className="num">
                              {hrs(t.share)}
                              {t.assets > 1 && <div className="xs muted">1 of {num(t.assets)}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DrawerSection>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
